import { useEffect, useMemo } from 'react';
import { areObjectIdsEqual } from 'helpers/idComparison';
import type { InspectionModel } from 'shared/domain/inspection/inspectionModel';
import type { IssueModel } from 'shared/domain/issue/issueModel';
import { useProjectTimezone } from 'store/project';
import { useIssueStore } from '../store';

/** Opens the issues of the inspection for as long as it is shown. */
export function useInspectionIssuesLoader(inspectionId: string | undefined): void {
  const timezone = useProjectTimezone();
  const openInspectionIssues = useIssueStore.use.openInspectionIssues();

  useEffect(
    () => (inspectionId ? openInspectionIssues(inspectionId, timezone) : undefined),
    [inspectionId, timezone, openInspectionIssues]
  );
}

export function useInspectionIssues(): { issues: IssueModel[]; loading: boolean } {
  const issues = useIssueStore.use.inspectionIssues();
  const loading = useIssueStore.use.inspectionIssuesLoading();
  return { issues, loading };
}

export function useProtocolItemIssuesList(protocolItemId: string | undefined): {
  issues: IssueModel[];
  loading: boolean;
} {
  const { issues, loading } = useInspectionIssues();
  const protocolIssues = useMemo(
    () => (protocolItemId ? issues.filter((issue) => areObjectIdsEqual(issue.protocolItem, protocolItemId)) : []),
    [issues, protocolItemId]
  );
  return { issues: protocolIssues, loading };
}

/** The inspection's count until its issues are read, then the issues read. */
export function useInspectionRelatedIssuesCount(inspectionModel: InspectionModel | undefined): number {
  const { issues, loading } = useInspectionIssues();
  if (!inspectionModel) {
    return 0;
  }
  return loading ? inspectionModel.relatedIssuesCount ?? 0 : issues.length;
}
